import { ObjectsFactory, Model, IResponse, IPaginateResponse } from './core'
import * as yup from 'yup'
import { IBank, IFile, IHttpError, IUser } from '.'
import { useMutation } from '@tanstack/react-query'

export enum CompanyStatusEnum {
  Active = 'active',
  Disable = 'disable'
}

export enum PaymentPartnerEnum {
  VnptEpay = 'VNPT_EPAY',
  Shinhan = 'SHINHAN'
}

export interface IContract {
  id: number
  company_id: number
  end_date: string
  start_date: string
  status: string
}

export interface ICompany {
  id: number
  created_at: string
  updated_at?: string
  created_by: number
  updated_by?: number
  name: string
  short_name: string
  system_code: string
  company_code: string
  code: string
  business_number: string
  address: string
  phone: string
  email: string
  business_registration: number
  bank_id: number
  bank_account_number: string
  establish_date: string
  company_type: string
  poc_firstname: string
  poc_lastname: string
  poc_phone: string
  total_user: number
  working_rate: string
  working_type: string
  setting_updated_at?: string
  setting_updated_by: any
  logo_id?: string
  status: CompanyStatusEnum
  department: string
  logo_file?: IFile
  created_user: IUser
  poc_phone_code: string
  updated_user: any
  active_contract: IContract
  bank?: IBank
  minimum_working_days: number
  withdraw_cutoff_day: number
  closing_date: number
  closing_date_type: string
  closing_specific_date: string
  memo: string
  current_pay_period: any
  payment_partner: PaymentPartnerEnum
}

export interface ICompanyCreatePayload {
  name: string
  short_name?: string
}

export interface ICompanyUpdatePayload {
  name: string
  status: CompanyStatusEnum
}

export interface ICompanyUpdateStatusPayload {
  status: CompanyStatusEnum
}

export interface IImportCompanyError {
  row: number
  error: string
  data: string[]
}

export interface IImportCompanyData {
  message: string
  totalProcessed: number
  successCount: number
  errorCount: number
  errors: IImportCompanyError[]
}

export interface IImportCompanyResponse {
  message: string
  data: IImportCompanyData
}

export enum ClosingDateTypeEnum {
  Before = 'before',
  After = 'after'
}

export enum ClosingDateSpecificDayEnum {
  FirstMonth = 'first_month',
  EndMonth = 'end_month',
  EnterDate = 'enter_date'
}

const modelConfig = {
  path: '/companies'
}

export class Company extends Model {
  static queryKeys = {
    paginate: 'COMPANY_PAGINATE_QUERY',
    findOne: 'COMPANY_FIND_ONE_QUERY'
  }

  static objects = ObjectsFactory.factory<ICompany>(modelConfig, this.queryKeys)

  static getCompaniesSelectOptions(roles: ICompany[] | undefined) {
    if (!roles) return []

    const options = roles.map(item => ({
      value: item.id,
      label: `${item.name}`
    }))

    return [...options]
  }
  static getBranchCreateSchemaValidation(__: (key: string) => string) {
    return yup.object().shape({
      branch_name: yup.string().required(__('Branch Name is required'))
    })
  }

  static updateCompanyById(id: number, payload: ICompanyCreatePayload) {
    return this.api.put<IResponse<ICompany>>({
      url: `/companies/${id}`,
      data: payload
    })
  }

  static updateCompanyStatus(id: number, payload: ICompanyUpdateStatusPayload) {
    return this.api.put<IResponse<ICompany>>({
      url: `/companies/${id}/status`,
      data: payload
    })
  }
  static importCompany(payload: any) {
    return this.api.post<IImportCompanyResponse>({
      url: `/companies/import`,
      data: payload
    })
  }

  static createCompany(payload: ICompany) {
    return this.api.post<IResponse<ICompany>>({
      url: `/companies`,
      data: payload
    })
  }
  static uploadContract(companyId: number, payload: any) {
    return this.api.upload<IResponse<ICompany>>({
      url: `/companies/${companyId}/contracts/upload`,
      data: payload
    })
  }
  static getActiveContract(companyId: number) {
    return {
      queryKey: ['ACTIVE_CONTRACT_QUERY'],
      queryFn: () => {
        return this.api
          .get<any>({
            url: `/companies/${companyId}/contracts/active`
          })
          .then(r => r.data)
      }
    }
  }
  static getNoteContract(companyId: number) {
    return {
      queryKey: ['NOTE_CONTRACT_QUERY'],
      queryFn: () => {
        return this.api.get<IResponse<any>>({ url: `/companies/${companyId}/notes` })
      }
    }
  }
  static createNoteContract(companyId: number, payload: { note: string }) {
    return this.api.post<IResponse<any>>({ url: `/companies/${companyId}/notes`, data: payload })
  }
  static deleteNoteContract(companyId: number, noteId: number) {
    return this.api.delete<IResponse<any>>({ url: `/companies/${companyId}/notes/${noteId}` })
  }
  static updateCompany(id: number, payload: ICompany) {
    return this.api.put<IResponse<ICompany>>({
      url: `/companies/${id}`,
      data: payload
    })
  }

  static getCompanyById(id: number) {
    return {
      queryKey: ['COMPANY_BY_ID_QUERY', id],
      queryFn: () => {
        return this.api
          .get<IResponse<ICompany>>({
            url: `/companies/${id}`
          })
          .then(r => r.data)
      }
    }
  }
  static deleteContract(companyId: number, contractId: number) {
    return this.api.delete<IResponse<ICompany>>({
      url: `/companies/${companyId}/contracts/${contractId}`
    })
  }
  static changeStatusCompany(payload: { status: CompanyStatusEnum; companyIds: number[] }) {
    return this.api.post<IResponse<ICompany>>({
      url: `/companies/change-status`,
      data: payload
    })
  }

  static getContractHistory(companyId: number, payload: any) {
    return {
      queryKey: ['CONTRACT_HISTORY_QUERY', companyId, payload],
      queryFn: () => {
        return this.api
          .get<IPaginateResponse<any>>({
            url: `/companies/${companyId}/contracts`,
            params: payload
          })
          .then(r => r.data)
      }
    }
  }

  static exportEmployer(companyIds?: number[]) {
    const params = companyIds && companyIds.length > 0 ? { ids: companyIds } : {}

    return this.api.downloadFile(`${modelConfig.path}/export`, 'employer_export.xlsx', { params })
  }

  static updateAvatarCompany(id: number, avatar: File) {
    return this.api.uploadFile<IResponse<ICompany>>(`${modelConfig.path}/${id}/update-avatar`, avatar)
  }
  static getImportTemplate() {
    return {
      queryKey: ['COMPANY_IMPORT_TEMPLATE_QUERY'],
      queryFn: () => {
        return this.api
          .get<Blob>({
            url: `/companies/import/template`,
            responseType: 'blob'
          })
          .then(r => r.data)
      }
    }
  }
}

Company.setup(modelConfig)

export function useCompanyStatusOptions() {
  const { __ } = useTranslator()

  return [
    { value: CompanyStatusEnum.Active, label: __('Active') },
    { value: CompanyStatusEnum.Disable, label: __('Disable') }
  ]
}

export function useBranchStatusColors() {
  return {
    [CompanyStatusEnum.Active]: 'success',
    [CompanyStatusEnum.Disable]: 'secondary'
  } as const
}

export function useCompanyStatusMutation() {
  return useMutation<IResponse<ICompany>, IHttpError, { id: number; data: ICompanyUpdateStatusPayload }>({
    mutationFn: ({ id, data }) => {
      return Company.updateCompanyStatus(id, data).then(res => res.data)
    }
  })
}

export function useCompanyImportMutation() {
  return useMutation<IImportCompanyResponse, IHttpError, { data: FormData }>({
    mutationFn: ({ data }) => Company.importCompany(data).then(res => res.data)
  })
}

export function useCompanyCreateMutation() {
  return useMutation<IResponse<ICompany>, IHttpError, { data: ICompany }>({
    mutationFn: ({ data }) => {
      return Company.createCompany(data).then(res => res.data)
    }
  })
}

export function useCompanyUploadContractMutation() {
  return useMutation<IResponse<ICompany>, IHttpError, { companyId: number; data: any }>({
    mutationFn: ({ companyId, data }) => {
      return Company.uploadContract(companyId, data).then(res => res.data)
    }
  })
}

export function useCompanyCreateNoteContractMutation() {
  return useMutation<IResponse<ICompany>, IHttpError, { companyId: number; data: { note: string } }>({
    mutationFn: ({ companyId, data }) => {
      return Company.createNoteContract(companyId, data).then(res => res.data)
    }
  })
}

export function useCompanyDeleteNoteContractMutation() {
  return useMutation<IResponse<ICompany>, IHttpError, { companyId: number; noteId: number }>({
    mutationFn: ({ companyId, noteId }) => {
      return Company.deleteNoteContract(companyId, noteId).then(res => res.data)
    }
  })
}

export function useCompanyUpdateMutation() {
  return useMutation<IResponse<ICompany>, IHttpError, { id: number; data: ICompany }>({
    mutationFn: ({ id, data }) => {
      return Company.updateCompany(id, data).then(res => res.data)
    }
  })
}

export function useCompanyDeleteContractMutation() {
  return useMutation<IResponse<ICompany>, IHttpError, { companyId: number; contractId: number }>({
    mutationFn: ({ companyId, contractId }) => {
      return Company.deleteContract(companyId, contractId).then(res => res.data)
    }
  })
}

export function useCompanyChangeStatusMutation() {
  return useMutation<IResponse<ICompany>, IHttpError, { data: { status: CompanyStatusEnum; companyIds: number[] } }>({
    mutationFn: ({ data }) => {
      return Company.changeStatusCompany(data).then(res => res.data)
    }
  })
}

export function useExportEmployeerMutation() {
  return useMutation<void, IHttpError, number[] | undefined>({
    mutationFn: userIds => {
      return Company.exportEmployer(userIds)
    }
  })
}

export function useUpdateAvatarCompanyMutation() {
  return useMutation<IResponse<ICompany>, IHttpError, { id: number; avatar: File }>({
    mutationFn: ({ id, avatar }) => {
      return Company.updateAvatarCompany(id, avatar).then(res => res.data)
    }
  })
}
